import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { ConfigService } from './config-service';

export interface LoginRequest {
  passwordLinkGuid: string;
  password: string;
}

export interface LoginResponse {
  token: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';

  /**
   * Getter ensures apiBaseUrl is read after APP_INITIALIZER has finished
   * loading lmsConfig.json — not at construction time.
   */
  private get baseUrl(): string {
    return this.configService.get('apiBaseUrl') || '';
  }

  constructor(private http: HttpClient, private configService: ConfigService) {}

  /**
   * Authenticates with the backend using the GUID from the email link and
   * the user-supplied password. On success, stores the returned JWT in
   * sessionStorage (never localStorage – cleared on tab/window close).
   */
  login(passwordLinkGuid: string, password: string): Observable<LoginResponse> {
    const payload: LoginRequest = { passwordLinkGuid, password };
    return this.http
      .post<LoginResponse>(`${this.baseUrl}login`, payload)
      .pipe(
        tap(response => {
          if (response && response.token) {
            sessionStorage.setItem(this.TOKEN_KEY, response.token);
          }
        }),
        catchError((err: HttpErrorResponse) => throwError(err))
      );
  }

  /** Returns the stored JWT, or null if not authenticated. */
  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  /** True only when a token is present; backend must still validate it. */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return token !== null && token.length > 0;
  }

  checkPasswordValidaOrNot(passwordLinkGuid: any, password: any): Observable<any> {
    return this.http
      .post(
        this.baseUrl + 'CheckPasswordValidOrNot?passwordLinkGuid=' + passwordLinkGuid + '&encyptedPassword=' + btoa(password),
        null
      )
      .pipe(
        tap((response: any) => {
          if (response && response.data && response.data.errorCode === 1) {
            const token = (response.data.token) ? response.data.token : passwordLinkGuid;
            sessionStorage.setItem(this.TOKEN_KEY, token);
          }
        }),
        catchError((err: HttpErrorResponse) => throwError(err)));
  }

  /** Clears the JWT from sessionStorage, effectively logging the user out. */
  logout(): void {
    sessionStorage.removeItem(this.TOKEN_KEY);
  }
}
